import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Segmented,
  Select,
  Typography,
  message,
} from "antd";
import {
  AppstoreOutlined,
  BarChartOutlined,
  BellOutlined,
  BookOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  HistoryOutlined,
  MoonOutlined,
  PaperClipOutlined,
  PictureOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  SunOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import type { TextAreaProps } from "antd/es/input";
import { useLocation, useNavigate } from "react-router-dom";
import type {
  ReviewCategory,
  RewriteExecuteResponse,
  RewriteMode,
  RewritePreviewResponse,
  ScanResponse,
} from "../types";
import {
  ApiError,
  appendConversationMessage,
  chatReplyStream,
  createConversation,
  deleteConversation,
  fetchComplianceAccess,
  fetchBillingSummary,
  fetchComplianceCredits,
  fetchConversationMessages,
  fetchConversations,
  getScanProgressStepLabels,
  rewriteExecute,
  rewritePreview,
  scanClaims,
  type ChatContextMessage,
  type ConversationSummary,
} from "../services/compliance";
import { AppHeaderLogo } from "../components/AppHeaderLogo";
import { ScanProgressModal } from "../components/ScanProgressModal";
import { GlobalUserMenu } from "../components/GlobalUserMenu";
import { useUiState } from "../state";
import { textMap } from "../i18n";

const { TextArea } = Input;
const { Text } = Typography;

type ChatRole = "user" | "assistant";

interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  review?: ScanResponse;
  streaming?: boolean;
}

interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  backendId?: string;
  updatedAt?: number;
  historyOffset?: number;
  hasMoreHistory?: boolean;
  loadingHistory?: boolean;
}

type ReviewTone = "high" | "medium" | "low";

const REVIEW_STORE_PREFIX = "__XTOOL_REVIEW__:";
const MESSAGE_PAGE_SIZE = 10;

type MessageRow = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  created_at?: string;
};

function buildAssistantReply(result: ScanResponse, lang: "zh" | "en"): string {
  if (!result.flaggedItems.length) {
    return lang === "zh"
      ? `合规分 ${result.score}/100。未发现明显高风险宣称，可继续人工复核并补充免责声明。`
      : `Compliance score ${result.score}/100. No obvious high-risk claims found. Please run manual review with proper disclaimers.`;
  }

  const top = result.flaggedItems[0];
  return lang === "zh"
    ? `合规分 ${result.score}/100。\n风险片段：${top.phrase}\n原因：${top.reason}\n建议：${top.suggestion}`
    : `Compliance score ${result.score}/100.\nRisky claim: ${top.phrase}\nReason: ${top.reason}\nSuggestion: ${top.suggestion}`;
}

function renderReviewTitle(lang: "zh" | "en", score: number) {
  return lang === "zh"
    ? `合规分 ${score}/100`
    : `Compliance Score ${score}/100`;
}

function getReviewTone(review: ScanResponse): ReviewTone {
  if (!review.flaggedItems.length) {
    return "low";
  }
  if (review.flaggedItems.some((item) => item.risk === "high")) {
    return "high";
  }
  return "medium";
}

function renderReviewSummary(review: ScanResponse, lang: "zh" | "en"): string {
  if (!review.flaggedItems.length) {
    return lang === "zh"
      ? "未发现明显高风险宣称，可继续人工复核并补充免责声明。"
      : "No obvious high-risk claims found. Please run manual review with proper disclaimers.";
  }
  return review.summary;
}

function renderRewriteResultText(
  data: RewriteExecuteResponse,
  lang: "zh" | "en",
): string {
  const lines: string[] = [
    lang === "zh" ? "改写完成（Pro）" : "Rewrite completed (Pro)",
    `${lang === "zh" ? "模式" : "Mode"}: ${data.mode}`,
    "",
    data.rewrite_text,
  ];
  return lines.join("\n");
}

function buildMessageCopyText(
  msg: ChatMessage,
  lang: "zh" | "en",
  fallbackCategory: ReviewCategory,
): string {
  if (!msg.review) {
    return msg.content;
  }
  const lines: string[] = [
    renderReviewTitle(lang, msg.review.score),
    msg.review.summary,
  ];
  if (msg.review.fda?.evidence?.length) {
    lines.push(
      lang === "zh"
        ? `FDA 证据命中：${msg.review.fda.evidence.length} 条（${msg.review.fda.category ?? fallbackCategory}）`
        : `FDA evidence hits: ${msg.review.fda.evidence.length} (${msg.review.fda.category ?? fallbackCategory})`,
    );
  }
  if (msg.review.fda?.endpoint_status) {
    lines.push(
      lang === "zh"
        ? `监管数据状态：${msg.review.fda.endpoint_status} / ${msg.review.fda.source}`
        : `Regulatory data status: ${msg.review.fda.endpoint_status} / ${msg.review.fda.source}`,
    );
  }
  msg.review.flaggedItems.forEach((item, idx) => {
    lines.push(
      `${lang === "zh" ? "问题" : "Problem"} ${idx + 1}: ${item.phrase}`,
    );
    lines.push(`${lang === "zh" ? "原因" : "Reason"}: ${item.reason}`);
    lines.push(`${lang === "zh" ? "建议" : "Suggestion"}: ${item.suggestion}`);
  });
  return lines.join("\n");
}

function makeSession(id: string, title: string, welcome: string): ChatSession {
  return {
    id,
    title,
    updatedAt: Date.now(),
    messages: [{ id: `${id}-welcome`, role: "assistant", content: welcome }],
  };
}

function parseUpdatedAt(value?: string): number {
  if (!value) {
    return 0;
  }
  const ts = Date.parse(value);
  return Number.isNaN(ts) ? 0 : ts;
}

function sortSessionsByUpdatedAt(items: ChatSession[]): ChatSession[] {
  return [...items].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
}

function encodeStoredReviewMessage(
  review: ScanResponse,
  lang: "zh" | "en",
): string {
  return `${REVIEW_STORE_PREFIX}${JSON.stringify({
    review,
    text: buildAssistantReply(review, lang),
  })}`;
}

function parseLegacyReviewText(content: string): ScanResponse | undefined {
  const en =
    /Compliance score\s+(\d+)\/100\.\s*\nRisky claim:\s*([\s\S]*?)\nReason:\s*([\s\S]*?)\nSuggestion:\s*([\s\S]*)$/i.exec(
      content.trim(),
    );
  if (en) {
    return {
      score: Number(en[1]),
      summary: `Compliance score ${en[1]}/100.`,
      flaggedItems: [
        {
          phrase: en[2].trim(),
          risk: "high",
          reason: en[3].trim(),
          suggestion: en[4].trim(),
        },
      ],
    };
  }
  const zh =
    /合规分\s*(\d+)\/100[。.]?\s*\n风险片段：\s*([\s\S]*?)\n原因：\s*([\s\S]*?)\n建议：\s*([\s\S]*)$/i.exec(
      content.trim(),
    );
  if (zh) {
    return {
      score: Number(zh[1]),
      summary: `合规分 ${zh[1]}/100。`,
      flaggedItems: [
        {
          phrase: zh[2].trim(),
          risk: "high",
          reason: zh[3].trim(),
          suggestion: zh[4].trim(),
        },
      ],
    };
  }
  return undefined;
}

function decodeStoredAssistantContent(content: string): {
  content: string;
  review?: ScanResponse;
} {
  if (content.startsWith(REVIEW_STORE_PREFIX)) {
    const json = content.slice(REVIEW_STORE_PREFIX.length);
    try {
      const parsed = JSON.parse(json) as {
        review?: ScanResponse;
        text?: string;
      };
      if (parsed.review) {
        return {
          content: parsed.text ?? buildAssistantReply(parsed.review, "en"),
          review: parsed.review,
        };
      }
    } catch {
      // ignore and fallback to plain text
    }
  }
  const legacy = parseLegacyReviewText(content);
  if (legacy) {
    return {
      content,
      review: legacy,
    };
  }
  return { content };
}

function sortMessagesStable(rows: MessageRow[]): MessageRow[] {
  const roleRank = (role: MessageRow["role"]) => {
    if (role === "user") return 0;
    if (role === "assistant") return 1;
    return 2;
  };
  return [...rows].sort((a, b) => {
    const at = a.created_at ? Date.parse(a.created_at) : 0;
    const bt = b.created_at ? Date.parse(b.created_at) : 0;
    if (at !== bt) {
      return at - bt;
    }
    const ar = roleRank(a.role);
    const br = roleRank(b.role);
    if (ar !== br) {
      return ar - br;
    }
    return a.id.localeCompare(b.id);
  });
}

function legacyCopyFallback(text: string): boolean {
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "true");
    area.style.position = "fixed";
    area.style.opacity = "0";
    area.style.left = "-9999px";
    document.body.appendChild(area);
    area.focus();
    area.select();
    area.setSelectionRange(0, area.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

export function ChatPage() {
  const { lang, themeMode, toggleLang, toggleTheme } = useUiState();
  const t = textMap[lang];
  const navigate = useNavigate();
  const location = useLocation();
  const [appMessage, contextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();
  const [draft, setDraft] = useState("");
  useEffect(() => {
    const draftFromStudio = (location.state as { studioDraft?: string } | null)
      ?.studioDraft;
    if (!draftFromStudio) return;
    setDraft(draftFromStudio);
    navigate(".", { replace: true, state: {} });
  }, [location.state, navigate]);
  const [sending, setSending] = useState(false);
  const [activeAction, setActiveAction] = useState<"chat" | "review" | null>(
    null,
  );
  const [reviewCredits, setReviewCredits] = useState(0);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [canUseStudio, setCanUseStudio] = useState(false);
  const [canUseRewrite, setCanUseRewrite] = useState(false);
  const [billingReady, setBillingReady] = useState(false);
  const [compactShell, setCompactShell] = useState(false);
  const [sessions, setSessions] = useState<ChatSession[]>([
    makeSession(
      "chat-1",
      lang === "zh" ? "新会话" : "New Chat",
      t.assistantWelcome,
    ),
  ]);
  const [activeSessionId, setActiveSessionId] = useState("chat-1");
  const [reviewCategory, setReviewCategory] =
    useState<ReviewCategory>("supplement");
  const [rewriteModalOpen, setRewriteModalOpen] = useState(false);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [rewriteSubmitting, setRewriteSubmitting] = useState(false);
  const [rewriteMode, setRewriteMode] = useState<RewriteMode>("balanced");
  const [rewriteSourceText, setRewriteSourceText] = useState("");
  const [rewritePreviewData, setRewritePreviewData] =
    useState<RewritePreviewResponse | null>(null);
  const chatStreamRef = useRef<HTMLDivElement | null>(null);
  const chatBottomRef = useRef<HTMLDivElement | null>(null);
  const suppressAutoScrollRef = useRef(false);
  const [reviewProgressLabel, setReviewProgressLabel] = useState("");
  const [reviewScanPercent, setReviewScanPercent] = useState(0);
  const [reviewScanActiveStep, setReviewScanActiveStep] = useState(0);
  const [reviewScanComplete, setReviewScanComplete] = useState(false);
  const reviewScanSteps = useMemo(() => getScanProgressStepLabels(lang), [lang]);

  const startReviewProgress = () => {
    setReviewScanActiveStep(0);
    setReviewScanPercent(8);
    setReviewScanComplete(false);
    setReviewProgressLabel(
      lang === "zh" ? "正在连接审查服务…" : "Connecting to compliance engine…",
    );
  };

  const resetReviewProgress = () => {
    setReviewScanActiveStep(0);
    setReviewScanPercent(0);
    setReviewScanComplete(false);
    setReviewProgressLabel("");
  };

  const appendAssistantNotice = (sessionId: string, text: string) => {
    const now = Date.now();
    const notice: ChatMessage = {
      id: `${sessionId}-err-${now}`,
      role: "assistant",
      content: text,
    };
    setSessions((prev) =>
      sortSessionsByUpdatedAt(prev.map((session) =>
        session.id === sessionId
          ? { ...session, updatedAt: now, messages: [...session.messages, notice] }
          : session,
      )),
    );
  };

  useEffect(() => {
    const load = async () => {
      const [credits, access] = await Promise.all([
        fetchComplianceCredits(),
        fetchComplianceAccess(),
      ]);
      if (credits) {
        setReviewCredits(credits.credit_balance);
        setIsUnlimited(Boolean(credits.is_unlimited));
      } else {
        const summary = await fetchBillingSummary();
        if (summary) {
          setReviewCredits(summary.credit_balance);
          setIsUnlimited(Boolean(summary.is_unlimited));
        } else {
          setIsUnlimited(false);
        }
      }
      setCanUseStudio(Boolean(access?.can_use_studio));
      setCanUseRewrite(Boolean(access?.can_use_rewrite));
      setBillingReady(true);
      const saved = await fetchConversations();
      if (!saved.length) {
        return;
      }
      const mappedSessions: ChatSession[] = saved.map(
        (item: ConversationSummary) => ({
          id: item.id,
          title: item.title,
          messages: [],
          backendId: item.id,
          updatedAt: parseUpdatedAt(item.updated_at),
          historyOffset: 0,
          hasMoreHistory: false,
          loadingHistory: false,
        }),
      );
      const sortedMapped = sortSessionsByUpdatedAt(mappedSessions);
      setSessions(sortedMapped);
      setActiveSessionId(sortedMapped[0].id);
      const firstPage = await fetchConversationMessages(sortedMapped[0].id, {
        limit: MESSAGE_PAGE_SIZE,
        offset: 0,
      });
      const firstSorted = sortMessagesStable(firstPage.items as MessageRow[]);
      setSessions((prev) =>
        sortSessionsByUpdatedAt(prev.map((session) =>
          session.id === sortedMapped[0].id
            ? {
                ...session,
                messages: firstSorted.map((m) => {
                  const decoded =
                    m.role === "assistant"
                      ? decodeStoredAssistantContent(m.content)
                      : { content: m.content };
                  return {
                    id: m.id,
                    role: m.role === "system" ? "assistant" : m.role,
                    content: decoded.content,
                    review: decoded.review,
                  };
                }),
                historyOffset: firstPage.next_offset,
                hasMoreHistory: firstPage.has_more,
                loadingHistory: false,
              }
            : session,
        )),
      );
    };
    void load();
  }, [lang]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mq = window.matchMedia("(max-width: 992px)");
    const apply = () => setCompactShell(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const activeSession = useMemo(
    () => sessions.find((item) => item.id === activeSessionId) ?? sessions[0],
    [activeSessionId, sessions],
  );

  const lastMessageSignature = useMemo(() => {
    const messages = activeSession?.messages ?? [];
    const last = messages[messages.length - 1];
    if (!last) {
      return "empty";
    }
    return `${messages.length}-${last.id}-${last.content.length}-${last.streaming ? "1" : "0"}`;
  }, [activeSession]);

  const hasVisibleStreaming = useMemo(
    () =>
      (activeSession?.messages ?? []).some(
        (item) => item.streaming && item.content.trim().length > 0,
      ),
    [activeSession],
  );

  useEffect(() => {
    if (suppressAutoScrollRef.current) {
      suppressAutoScrollRef.current = false;
      return;
    }
    const scroller = chatStreamRef.current;
    if (!scroller) {
      return;
    }
    scroller.scrollTop = scroller.scrollHeight;
  }, [activeSessionId, lastMessageSignature, sending]);

  useEffect(() => {
    if (!rewriteModalOpen || !rewriteSourceText.trim()) {
      return;
    }
    let cancelled = false;
    const loadPreview = async () => {
      setRewriteLoading(true);
      try {
        const preview = await rewritePreview(
          rewriteSourceText,
          lang,
          reviewCategory,
          rewriteMode,
        );
        if (!cancelled) {
          setRewritePreviewData(preview);
        }
      } catch {
        if (!cancelled) {
          setRewritePreviewData(null);
        }
      } finally {
        if (!cancelled) {
          setRewriteLoading(false);
        }
      }
    };
    void loadPreview();
    return () => {
      cancelled = true;
    };
  }, [lang, reviewCategory, rewriteModalOpen, rewriteMode, rewriteSourceText]);

  const createNewSession = async () => {
    const localId = `chat-${Date.now()}`;
    const defaultTitle = lang === "zh" ? "新会话" : "New Chat";
    try {
      const created = await createConversation(defaultTitle);
      const next: ChatSession = {
        id: created.id,
        title: created.title,
        backendId: created.id,
        updatedAt: parseUpdatedAt(created.updated_at) || Date.now(),
        historyOffset: 0,
        hasMoreHistory: false,
        loadingHistory: false,
        messages: [
          {
            id: `${created.id}-welcome`,
            role: "assistant",
            content: t.assistantWelcome,
          },
        ],
      };
      setSessions((prev) => sortSessionsByUpdatedAt([next, ...prev]));
      setActiveSessionId(next.id);
    } catch {
      const next = makeSession(localId, defaultTitle, t.assistantWelcome);
      setSessions((prev) => sortSessionsByUpdatedAt([next, ...prev]));
      setActiveSessionId(localId);
      appMessage.warning(
        lang === "zh"
          ? "会话创建失败，已切换本地会话"
          : "Failed to create conversation on server, switched to local session",
      );
    }
    setDraft("");
  };

  const onSelectSession = async (sessionId: string) => {
    setActiveSessionId(sessionId);
    const target = sessions.find((s) => s.id === sessionId);
    if (!target?.backendId || target.messages.length > 1) {
      return;
    }
    const page = await fetchConversationMessages(target.backendId, {
      limit: MESSAGE_PAGE_SIZE,
      offset: 0,
    });
    const sortedRows = sortMessagesStable(page.items as MessageRow[]);
    setSessions((prev) =>
      prev.map((session) =>
        session.id === sessionId
          ? {
              ...session,
              messages: sortedRows.map((m) => {
                const decoded =
                  m.role === "assistant"
                    ? decodeStoredAssistantContent(m.content)
                    : { content: m.content };
                return {
                  id: m.id,
                  role: m.role === "system" ? "assistant" : m.role,
                  content: decoded.content,
                  review: decoded.review,
                };
              }),
              historyOffset: page.next_offset,
              hasMoreHistory: page.has_more,
              loadingHistory: false,
            }
          : session,
      ),
    );
  };

  const handleDeleteSession = (session: ChatSession) => {
    modal.confirm({
      title: lang === "zh" ? "删除此对话？" : "Delete this conversation?",
      content:
        lang === "zh"
          ? "删除后将无法恢复，确认继续吗？"
          : "This will permanently remove all messages in this conversation.",
      okText: lang === "zh" ? "删除" : "Delete",
      okButtonProps: { danger: true },
      cancelText: lang === "zh" ? "取消" : "Cancel",
      onOk: async () => {
        if (session.backendId) {
          await deleteConversation(session.backendId);
        }
        const fallbackId = `chat-${Date.now()}`;
        const remaining = sessions.filter((item) => item.id !== session.id);
        const nextActiveId =
          activeSessionId === session.id
            ? (remaining[0]?.id ?? fallbackId)
            : activeSessionId;
        setSessions((prev) => {
          const next = prev.filter((item) => item.id !== session.id);
          if (next.length) {
            return next;
          }
          return [
            makeSession(
              fallbackId,
              lang === "zh" ? "新会话" : "New Chat",
              t.assistantWelcome,
            ),
          ];
        });
        setActiveSessionId(nextActiveId);
      },
    });
  };

  const loadOlderMessages = async () => {
    const session = sessions.find((s) => s.id === activeSessionId);
    const scroller = chatStreamRef.current;
    if (
      !session?.backendId ||
      !scroller ||
      session.loadingHistory ||
      !session.hasMoreHistory
    ) {
      return;
    }
    const prevHeight = scroller.scrollHeight;
    const prevTop = scroller.scrollTop;
    setSessions((prev) =>
      prev.map((s) =>
        s.id === activeSessionId ? { ...s, loadingHistory: true } : s,
      ),
    );
    try {
      const page = await fetchConversationMessages(session.backendId, {
        limit: MESSAGE_PAGE_SIZE,
        offset: session.historyOffset ?? 0,
      });
      const olderSorted = sortMessagesStable(page.items as MessageRow[]);
      suppressAutoScrollRef.current = true;
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== activeSessionId) {
            return s;
          }
          const olderMapped = olderSorted.map((m) => {
            const decoded =
              m.role === "assistant"
                ? decodeStoredAssistantContent(m.content)
                : { content: m.content };
            return {
              id: m.id,
              role: m.role === "system" ? "assistant" : m.role,
              content: decoded.content,
              review: decoded.review,
            } as ChatMessage;
          });
          const seen = new Set<string>();
          const merged = [...olderMapped, ...s.messages].filter((m) => {
            if (seen.has(m.id)) {
              return false;
            }
            seen.add(m.id);
            return true;
          });
          return {
            ...s,
            messages: merged,
            historyOffset: page.next_offset,
            hasMoreHistory: page.has_more,
            loadingHistory: false,
          };
        }),
      );
      requestAnimationFrame(() => {
        const updated = chatStreamRef.current;
        if (!updated) {
          return;
        }
        updated.scrollTop = updated.scrollHeight - prevHeight + prevTop;
      });
    } catch {
      setSessions((prev) =>
        prev.map((s) =>
          s.id === activeSessionId ? { ...s, loadingHistory: false } : s,
        ),
      );
    }
  };

  const handleChatStreamScroll = () => {
    const scroller = chatStreamRef.current;
    if (!scroller || scroller.scrollTop > 36) {
      return;
    }
    void loadOlderMessages();
  };

  const sendMessage = async () => {
    const content = draft.trim();
    if (!content || !activeSession) {
      return;
    }

    const userMsg: ChatMessage = {
      id: `${activeSession.id}-u-${Date.now()}`,
      role: "user",
      content,
    };

    setDraft("");
    setSending(true);
    setActiveAction("chat");
    const touchedAt = Date.now();

    setSessions((prev) =>
      sortSessionsByUpdatedAt(prev.map((session) =>
        session.id === activeSession.id
          ? {
              ...session,
              updatedAt: touchedAt,
              title:
                session.messages.length <= 1
                  ? content.slice(0, 20)
                  : session.title,
              messages: [...session.messages, userMsg],
            }
          : session,
      )),
    );

    try {
      const contextMessages: ChatContextMessage[] = [
        ...activeSession.messages,
        userMsg,
      ]
        .slice(-12)
        .map((item) => ({ role: item.role, content: item.content }));
      const assistantMsgId = `${activeSession.id}-a-${Date.now()}`;
      const assistantMsg: ChatMessage = {
        id: assistantMsgId,
        role: "assistant",
        content: "",
        streaming: true,
      };
      setSessions((prev) =>
        prev.map((session) =>
          session.id === activeSession.id
            ? { ...session, messages: [...session.messages, assistantMsg] }
            : session,
        ),
      );

      const response = await chatReplyStream(
        content,
        lang,
        contextMessages,
        activeSession.backendId,
        (chunk) => {
          setSessions((prev) =>
            prev.map((session) =>
              session.id === activeSession.id
                ? {
                    ...session,
                    messages: session.messages.map((m) =>
                      m.id === assistantMsgId
                        ? { ...m, content: m.content + chunk }
                        : m,
                    ),
                  }
                : session,
            ),
          );
        },
      );

      setSessions((prev) =>
        sortSessionsByUpdatedAt(prev.map((session) =>
          session.id === activeSession.id
            ? {
                ...session,
                updatedAt: Date.now(),
                backendId: response.conversationId ?? session.backendId,
                historyOffset:
                  typeof session.historyOffset === "number"
                    ? session.historyOffset + 2
                    : session.historyOffset,
                messages: session.messages.map((m) =>
                  m.id === assistantMsgId
                    ? {
                        ...m,
                        content: response.answer || m.content,
                        streaming: false,
                      }
                    : m,
                ),
              }
            : session,
        )),
      );
    } catch (error) {
      const errText =
        error instanceof Error && error.message
          ? error.message
          : lang === "zh"
            ? "分析失败，请稍后重试"
            : "Analysis failed, please try again";
      setSessions((prev) =>
        prev.map((session) =>
          session.id === activeSession.id
            ? {
                ...session,
                messages: session.messages.map((m) =>
                  m.streaming ? { ...m, streaming: false } : m,
                ),
              }
            : session,
        ),
      );
      appMessage.error(errText);
      appendAssistantNotice(
        activeSession.id,
        lang === "zh" ? `系统提示：${errText}` : `System notice: ${errText}`,
      );
    } finally {
      setSending(false);
      setActiveAction(null);
    }
  };

  const runReview = async () => {
    const content = draft.trim();
    if (!content || !activeSession) {
      return;
    }
    if (!isUnlimited && reviewCredits <= 0) {
      modal.confirm({
        title: t.outOfCredits,
        content: t.reviewUpsell,
        okText: t.upgradePlanNow,
        cancelText: t.later,
        onOk: () => navigate("/app/upgrade"),
      });
      return;
    }

    const userMsg: ChatMessage = {
      id: `${activeSession.id}-u-${Date.now()}`,
      role: "user",
      content,
    };

    setDraft("");
    setSending(true);
    setActiveAction("review");
    startReviewProgress();
    const touchedAt = Date.now();

    setSessions((prev) =>
      sortSessionsByUpdatedAt(prev.map((session) =>
        session.id === activeSession.id
          ? {
              ...session,
              updatedAt: touchedAt,
              title:
                session.messages.length <= 1
                  ? content.slice(0, 20)
                  : session.title,
              messages: [...session.messages, userMsg],
            }
          : session,
      )),
    );

    try {
      let conversationId = activeSession.backendId;
      if (!conversationId) {
        const created = await createConversation(
          activeSession.title || (lang === "zh" ? "新会话" : "New Chat"),
        );
        conversationId = created.id;
        setSessions((prev) =>
          sortSessionsByUpdatedAt(prev.map((session) =>
            session.id === activeSession.id
              ? {
                  ...session,
                  backendId: created.id,
                  title: created.title,
                  updatedAt: parseUpdatedAt(created.updated_at) || Date.now(),
                }
              : session,
          )),
        );
      }

      const result = await scanClaims(content, lang, reviewCategory, {
        onProgress: (p) => {
          setReviewProgressLabel(p.label);
          setReviewScanPercent(p.percent);
          setReviewScanActiveStep(
            p.isComplete ? reviewScanSteps.length : p.index,
          );
          setReviewScanComplete(Boolean(p.isComplete));
        },
      });
      const assistantMsg: ChatMessage = {
        id: `${activeSession.id}-a-${Date.now()}`,
        role: "assistant",
        content: buildAssistantReply(result, lang),
        review: result,
      };

      let persistedReviewMessages = false;
      if (conversationId) {
        try {
          await appendConversationMessage(
            conversationId,
            "user",
            userMsg.content,
          );
          await appendConversationMessage(
            conversationId,
            "assistant",
            encodeStoredReviewMessage(result, lang),
          );
          persistedReviewMessages = true;
        } catch {
          appMessage.warning(
            lang === "zh"
              ? "已完成审查，但会话保存失败"
              : "Review completed, but chat persistence failed",
          );
        }
      }

      setSessions((prev) =>
        sortSessionsByUpdatedAt(prev.map((session) =>
          session.id === activeSession.id
            ? {
                ...session,
                updatedAt: Date.now(),
                historyOffset:
                  persistedReviewMessages &&
                  typeof session.historyOffset === "number"
                    ? session.historyOffset + 2
                    : session.historyOffset,
                messages: [...session.messages, assistantMsg],
              }
            : session,
        )),
      );
      if (typeof result.is_unlimited === "boolean") {
        setIsUnlimited(result.is_unlimited);
      }
      if (typeof result.credit_balance === "number") {
        setReviewCredits(result.credit_balance);
      } else {
        const [credits, access] = await Promise.all([
          fetchComplianceCredits(),
          fetchComplianceAccess(),
        ]);
        if (credits) {
          setReviewCredits(credits.credit_balance);
          setIsUnlimited(Boolean(credits.is_unlimited));
        } else {
          const summary = await fetchBillingSummary();
          if (summary) {
            setReviewCredits(summary.credit_balance);
            setIsUnlimited(Boolean(summary.is_unlimited));
          }
        }
        if (access) {
          setCanUseStudio(Boolean(access.can_use_studio));
          setCanUseRewrite(Boolean(access.can_use_rewrite));
        }
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 402) {
        const text =
          lang === "zh"
            ? "当前审查额度不足，请升级套餐后继续执行深度合规审查。"
            : "No review credits left. Please upgrade your plan to continue deep compliance review.";
        modal.confirm({
          title: t.outOfCredits,
          content: t.reviewUpsell,
          okText: t.upgradePlanNow,
          cancelText: t.later,
          onOk: () => navigate("/app/upgrade"),
        });
        appendAssistantNotice(activeSession.id, text);
      } else {
        const errText =
          error instanceof Error && error.message
            ? error.message
            : lang === "zh"
              ? "分析失败，请稍后重试"
              : "Analysis failed, please try again";
        appMessage.error(errText);
        appendAssistantNotice(
          activeSession.id,
          lang === "zh" ? `系统提示：${errText}` : `System notice: ${errText}`,
        );
      }
    } finally {
      setSending(false);
      setActiveAction(null);
      resetReviewProgress();
    }
  };

  const getReviewSourceText = (
    sessionId: string,
    reviewMessageId: string,
  ): string => {
    const session = sessions.find((item) => item.id === sessionId);
    if (!session) {
      return "";
    }
    const idx = session.messages.findIndex((m) => m.id === reviewMessageId);
    if (idx <= 0) {
      return "";
    }
    for (let i = idx - 1; i >= 0; i -= 1) {
      const m = session.messages[i];
      if (m.role === "user" && m.content.trim()) {
        return m.content.trim();
      }
    }
    return "";
  };

  const openScanToolkit = () => {
    if (!billingReady) {
      return;
    }
    if (!canUseStudio) {
      modal.info({
        title: t.scanToolkitLockedTitle,
        content: t.scanToolkitLockedBody,
        okText: t.upgradePlanNow,
        onOk: () => navigate("/app/upgrade"),
      });
      return;
    }
    navigate("/studio");
  };

  const openRewrite = (sourceText: string) => {
    if (!canUseRewrite) {
      modal.confirm({
        title: lang === "zh" ? "改写功能需升级 Pro" : "Rewrite requires Pro",
        content:
          lang === "zh"
            ? "一键改写是 Pro 会员功能。请先升级 Pro 后使用。"
            : "One-click rewrite is a Pro feature. Please upgrade to Pro first.",
        okText: lang === "zh" ? "去升级 Pro" : "Upgrade to Pro",
        cancelText: lang === "zh" ? "稍后" : "Later",
        onOk: () => navigate("/app/upgrade"),
      });
      return;
    }
    if (!sourceText.trim()) {
      appMessage.warning(
        lang === "zh" ? "未找到可改写的原文" : "No source copy found",
      );
      return;
    }
    setRewriteSourceText(sourceText);
    setRewriteModalOpen(true);
    setRewritePreviewData(null);
    setRewriteLoading(true);
  };

  const executeRewritePaid = async () => {
    if (!activeSession || !rewriteSourceText.trim()) {
      return;
    }
    setRewriteSubmitting(true);
    try {
      const result = await rewriteExecute(
        rewriteSourceText,
        lang,
        reviewCategory,
        rewriteMode,
      );
      const assistantMsg: ChatMessage = {
        id: `${activeSession.id}-rw-${Date.now()}`,
        role: "assistant",
        content: renderRewriteResultText(result, lang),
      };
      setSessions((prev) =>
        sortSessionsByUpdatedAt(prev.map((session) =>
          session.id === activeSession.id
            ? {
                ...session,
                updatedAt: Date.now(),
                messages: [...session.messages, assistantMsg],
              }
            : session,
        )),
      );
      if (activeSession.backendId) {
        try {
          await appendConversationMessage(
            activeSession.backendId,
            "assistant",
            assistantMsg.content,
          );
        } catch {
          // ignore history append failure for rewrite reply
        }
      }
      setRewriteModalOpen(false);
    } catch (error) {
      if (
        error instanceof ApiError &&
        (error.status === 402 || error.status === 403)
      ) {
        modal.confirm({
          title: lang === "zh" ? "改写功能需升级 Pro" : "Rewrite requires Pro",
          content:
            lang === "zh"
              ? "一键改写是 Pro 会员功能。请先升级 Pro 后使用。"
              : "One-click rewrite is a Pro feature. Please upgrade to Pro first.",
          okText: lang === "zh" ? "去升级 Pro" : "Upgrade to Pro",
          cancelText: lang === "zh" ? "稍后" : "Later",
          onOk: () => navigate("/app/upgrade"),
        });
      } else {
        appMessage.error(
          error instanceof Error && error.message
            ? error.message
            : lang === "zh"
              ? "改写失败，请稍后重试"
              : "Rewrite failed, please retry",
        );
      }
    } finally {
      setRewriteSubmitting(false);
    }
  };

  const handleContinueAskFree = (sourceText: string) => {
    const prompt =
      lang === "zh"
        ? `请基于这段文案给我免费的方向建议（不要直接整段改写）：\n${sourceText}`
        : `Give me free directional advice for this copy (do not fully rewrite it):\n${sourceText}`;
    setDraft(prompt);
  };

  const handleCopyMessage = async (item: ChatMessage) => {
    const text = buildMessageCopyText(item, lang, reviewCategory).trim();
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      appMessage.success(lang === "zh" ? "已复制" : "Copied");
    } catch {
      const ok = legacyCopyFallback(text);
      if (ok) {
        appMessage.success(lang === "zh" ? "已复制" : "Copied");
      } else {
        appMessage.error(
          lang === "zh"
            ? "复制失败，请手动选中文本复制"
            : "Copy failed, please select text manually",
        );
      }
    }
  };

  const handleComposerPressEnter: TextAreaProps["onPressEnter"] = (event) => {
    if (event.shiftKey) {
      return;
    }
    const native = event.nativeEvent as KeyboardEvent;
    if (native.isComposing || sending) {
      return;
    }
    event.preventDefault();
    void sendMessage();
  };

  return (
    <>
      {contextHolder}
      {modalContextHolder}
      <ScanProgressModal
        open={sending && activeAction === "review"}
        title={t.executeReview}
        steps={reviewScanSteps}
        activeStepIndex={reviewScanActiveStep}
        percent={reviewScanPercent}
        statusLine={
          reviewProgressLabel ||
          (lang === "zh" ? "准备中…" : "Preparing…")
        }
        isComplete={reviewScanComplete}
        variant={themeMode === "dark" ? "dark" : "light"}
      />
      <div
        className={`sentinel-shell ${themeMode === "dark" ? "sentinel-dark" : "sentinel-light"}`}
      >
        <aside className="sentinel-sidebar">
          <div className="sentinel-brand" onClick={() => navigate("/")}>
            <div>
              <Text className="sentinel-brand-title">XTool</Text>
              <Text className="sentinel-brand-sub">
                {lang === "zh" ? "架构哨兵" : "Architectural Sentinel"}
              </Text>
            </div>
          </div>
          <Button
            block
            icon={<PlusOutlined />}
            className="sentinel-new-review"
            onClick={() => void createNewSession()}
          >
            {lang === "zh" ? "新建审查" : "New Review"}
          </Button>
          <p className="sentinel-sidebar-label">{t.chatHistoryTitle}</p>
          <nav className="sentinel-nav">
            <div className="sentinel-nav-history">
              <a className="sentinel-nav-item" href="#" onClick={(e) => e.preventDefault()}>
                <HistoryOutlined className="sentinel-nav-icon" />
                {lang === "zh" ? "历史" : "History"}
              </a>
              <div className="sentinel-nav-sublist">
                {sessions.map((item) => (
                  <div
                    key={item.id}
                    className={`sentinel-nav-subitem-wrap ${item.id === activeSession?.id ? "active" : ""}`}
                  >
                    <a
                      className="sentinel-nav-subitem"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        void onSelectSession(item.id);
                      }}
                    >
                      {item.title}
                    </a>
                    <Button
                      type="text"
                      size="small"
                      className="sentinel-nav-delete"
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteSession(item);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </nav>
          <div className="sentinel-sidebar-footer">
            <button
              type="button"
              className="sentinel-nav-item"
              onClick={openScanToolkit}
              disabled={!billingReady}
            >
              <ThunderboltOutlined className="sentinel-nav-icon" />
              {t.scanToolkitEntry}
            </button>
            <a
              className="sentinel-nav-item"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate("/app/dashboard");
              }}
            >
              <BarChartOutlined className="sentinel-nav-icon" />
              {lang === "zh" ? "个人中心" : "Workspace"}
            </a>
          </div>
        </aside>

        <div className="sentinel-main-wrap">
          <header className="sentinel-topbar">
            <div className="sentinel-topbar-left">
              <AppHeaderLogo className="sentinel-topbar-logo" />
              <div className="sentinel-category-wrap">
                <AppstoreOutlined className="sentinel-category-icon" />
                <Select
                  className="sentinel-category-select"
                  value={reviewCategory}
                  onChange={(value) => setReviewCategory(value as ReviewCategory)}
                  options={[
                    { value: "cosmetic", label: lang === "zh" ? "化妆品" : "Cosmetic" },
                    { value: "food", label: lang === "zh" ? "食品" : "Food" },
                    {
                      value: "supplement",
                      label: lang === "zh" ? "保健品" : "Supplement",
                    },
                  ]}
                  bordered={false}
                />
              </div>
              <div className="sentinel-topbar-divider" />
              <h2 className="sentinel-topbar-title">
                {activeSession?.title ||
                  (lang === "zh" ? "新审查会话" : "New review session")}
              </h2>
            </div>
            <div className="sentinel-topbar-right">
              {compactShell ? (
                <Button
                  type="default"
                  size="small"
                  icon={<ThunderboltOutlined />}
                  className="sentinel-toolkit-top-btn"
                  onClick={openScanToolkit}
                  disabled={!billingReady}
                >
                  {t.scanToolkitShort}
                </Button>
              ) : null}
              <Segmented
                size="small"
                value={lang}
                options={[
                  { label: "EN", value: "en" },
                  { label: "中文", value: "zh" },
                ]}
                onChange={() => toggleLang()}
              />
              <Button
                type="text"
                size="small"
                className="icon-btn-pill icon-btn-pill-circle"
                icon={themeMode === "dark" ? <SunOutlined /> : <MoonOutlined />}
                onClick={() => toggleTheme(themeMode !== "dark")}
              />
              <div className="sentinel-credit-pill">
                {t.creditsLabel}: {reviewCredits.toLocaleString()}
              </div>
              <Button type="text" className="sentinel-notify-btn" icon={<BellOutlined />} />
              <GlobalUserMenu />
            </div>
          </header>
          <main
            className="sentinel-chat-main"
            ref={chatStreamRef}
            onScroll={handleChatStreamScroll}
          >
            <div className="sentinel-chat-inner">
              {activeSession?.loadingHistory ? (
                <div className="sentinel-line assistant">
                  <div className="sentinel-assistant-bubble">
                    <Text type="secondary">
                      {lang === "zh"
                        ? "正在加载更早消息..."
                        : "Loading older messages..."}
                    </Text>
                  </div>
                </div>
              ) : null}
              {(activeSession?.messages ?? []).map((messageItem) => {
                if (messageItem.streaming && !messageItem.content.trim()) {
                  return null;
                }
                if (messageItem.role === "user") {
                  return (
                    <div key={messageItem.id} className="sentinel-line user">
                      <div className="sentinel-user-bubble">
                        <p className="sentinel-user-text">{messageItem.content}</p>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={messageItem.id} className="sentinel-line assistant">
                    <div className="sentinel-assistant-bubble">
                      {messageItem.review ? (
                        <div className="sentinel-review-card">
                          {(() => {
                            const reviewTone = getReviewTone(messageItem.review);
                            const ringStroke =
                              reviewTone === "high"
                                ? "var(--sentinel-error)"
                                : reviewTone === "medium"
                                  ? "#b7791f"
                                  : "#0b6b44";
                            const riskTagText =
                              lang === "zh"
                                ? reviewTone === "high"
                                  ? "需处理"
                                  : reviewTone === "medium"
                                    ? "建议处理"
                                    : "风险较低"
                                : reviewTone === "high"
                                  ? "Action Required"
                                  : reviewTone === "medium"
                                    ? "Review Suggested"
                                    : "Low Risk";
                            return (
                              <>
                          <div className="sentinel-review-head">
                            <div className="sentinel-score-wrap">
                              <div className="sentinel-score-ring-wrap">
                                <svg className="sentinel-score-svg" viewBox="0 0 64 64">
                                  <circle className="sentinel-score-bg" cx="32" cy="32" r="28" />
                                  <circle
                                    className="sentinel-score-bar"
                                    cx="32"
                                    cy="32"
                                    r="28"
                                    style={{
                                      stroke: ringStroke,
                                      strokeDasharray: 176,
                                      strokeDashoffset: 176 - (176 * messageItem.review.score) / 100,
                                    }}
                                  />
                                </svg>
                                <span className="sentinel-score-num">{messageItem.review.score}</span>
                              </div>
                              <div className="sentinel-review-texts">
                                <h3 className="sentinel-review-title-main">
                                  {renderReviewTitle(lang, messageItem.review.score)}
                                </h3>
                                <p className="sentinel-review-summary">
                                  {renderReviewSummary(messageItem.review, lang)}
                                </p>
                              </div>
                            </div>
                            <span
                              className={`sentinel-risk-tag ${reviewTone}`}
                            >
                              {riskTagText}
                            </span>
                          </div>

                          <div className="sentinel-findings-wrap">
                            {messageItem.review.flaggedItems.length ? (
                              messageItem.review.flaggedItems.map((item, idx) => (
                                <div
                                  key={`${messageItem.id}-risk-${idx}`}
                                  className="sentinel-finding-item"
                                >
                                  <div className="sentinel-finding-inner">
                                    <div className={`sentinel-finding-warning ${reviewTone}`}>
                                      <WarningOutlined />
                                    </div>
                                    <div className="sentinel-finding-content">
                                      <div className="sentinel-finding-head">
                                        <span className="sentinel-finding-label">
                                          {lang === "zh" ? "问题短语" : "Flagged Phrase"}
                                        </span>
                                        <span className="sentinel-finding-tag">&quot;{item.phrase}&quot;</span>
                                      </div>
                                      <p className="sentinel-finding-reason">
                                        <strong>{lang === "zh" ? "原因：" : "Reason:"}</strong> {item.reason}
                                      </p>
                                      <div className="sentinel-suggestion-box">
                                        <div className="sentinel-suggestion-head">
                                          <CheckCircleOutlined className="sentinel-suggestion-check" />
                                          <span>{lang === "zh" ? "建议" : "Recommended Suggestion"}</span>
                                        </div>
                                        <p className="sentinel-suggestion-text">&quot;{item.suggestion}&quot;</p>
                                      </div>
                                      {messageItem.review?.fda?.evidence?.length ? (
                                        <div className="sentinel-fda-row">
                                          <BookOutlined />
                                          <span>
                                            <strong>FDA Evidence:</strong>{" "}
                                            {(messageItem.review.fda.evidence[0] as { title?: string })?.title ??
                                              `CFR Title 21 - ${String(reviewCategory)} § 701.3`}
                                          </span>
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="sentinel-no-findings">
                                {lang === "zh"
                                  ? "本次审查未检出需要处理的问题项。"
                                  : "No flagged claims found in this review."}
                              </div>
                            )}
                          </div>
                              </>
                            );
                          })()}

                          <div className="sentinel-review-actions">
                            <Button
                              type="primary"
                              icon={<ThunderboltOutlined />}
                              className="sentinel-action-rewrite"
                              onClick={() =>
                                void openRewrite(
                                  getReviewSourceText(
                                    activeSession.id,
                                    messageItem.id,
                                  ),
                                )
                              }
                            >
                              {lang === "zh" ? "一键改写（Pro）" : "One-click Rewrite (Pro)"}
                            </Button>
                            <Button
                              icon={<SendOutlined />}
                              className="sentinel-action-continue"
                              onClick={() =>
                                handleContinueAskFree(
                                  getReviewSourceText(
                                    activeSession.id,
                                    messageItem.id,
                                  ),
                                )
                              }
                            >
                              {lang === "zh" ? "继续问AI（免费）" : "Continue asking AI (Free)"}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Text
                            className={
                              messageItem.streaming ? "streaming-text" : ""
                            }
                          >
                            {messageItem.content}
                          </Text>
                          <Button
                            size="small"
                            type="text"
                            className="sentinel-copy-btn"
                            icon={<CopyOutlined />}
                            onClick={() => void handleCopyMessage(messageItem)}
                          >
                            {lang === "zh" ? "复制" : "Copy"}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {sending && activeAction !== "review" && !hasVisibleStreaming ? (
                <div className="sentinel-line assistant">
                  <div className="sentinel-assistant-bubble">
                    <div className="feedback-loading">
                      <span className="dot" />
                      <span className="dot" />
                      <span className="dot" />
                      <Text type="secondary">{t.scanningReply}</Text>
                    </div>
                  </div>
                </div>
              ) : null}
              <div ref={chatBottomRef} />
            </div>
          </main>

          <footer className="sentinel-composer-wrap">
            <div className="sentinel-composer">
              <TextArea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onPressEnter={handleComposerPressEnter}
                placeholder={t.chatInputPlaceholder}
                rows={5}
                className="sentinel-composer-input"
              />
              <div className="sentinel-composer-actions">
                <div className="sentinel-composer-left">
                  <Button type="text" className="sentinel-composer-icon-btn" icon={<PaperClipOutlined />} disabled />
                  <Button type="text" className="sentinel-composer-icon-btn" icon={<PictureOutlined />} disabled />
                  <div className="sentinel-composer-divider" />
                  <span className="sentinel-composer-category-label">
                    {reviewCategory === "cosmetic"
                      ? lang === "zh"
                        ? "化妆品指南已启用"
                        : "Cosmetic Guidelines Active"
                      : reviewCategory === "food"
                        ? lang === "zh"
                          ? "食品指南已启用"
                          : "Food Guidelines Active"
                        : lang === "zh"
                          ? "保健品指南已启用"
                          : "Supplement Guidelines Active"}
                  </span>
                </div>
                <div className="sentinel-composer-right">
                  <Button
                    type="text"
                    className="sentinel-composer-send"
                    icon={<SendOutlined />}
                    loading={sending}
                    onClick={sendMessage}
                  />
                  <Button
                    type="primary"
                    icon={<SafetyCertificateOutlined />}
                    loading={sending}
                    onClick={runReview}
                    className="sentinel-run-btn"
                  >
                    {t.executeReview}
                  </Button>
                </div>
              </div>
            </div>
            <p className="sentinel-composer-footer">
              {lang === "zh"
                ? "法规数据更新：2 小时前。XTool 使用 AI 与 CFR 数据库。"
                : "Regulatory data updated: 2 hours ago. XTool uses AI and CFR databases."}
            </p>
          </footer>
        </div>
      </div>
      <Modal
        title={lang === "zh" ? "一键改写（Pro）" : "One-click Rewrite (Pro)"}
        open={rewriteModalOpen}
        onCancel={() => setRewriteModalOpen(false)}
        onOk={() => void executeRewritePaid()}
        okButtonProps={{ loading: rewriteSubmitting, disabled: rewriteLoading }}
        okText={lang === "zh" ? "确认执行完整改写" : "Run full rewrite"}
        cancelText={lang === "zh" ? "取消" : "Cancel"}
      >
        <div style={{ display: "grid", gap: 12 }}>
          <Select
            value={rewriteMode}
            onChange={(value) => setRewriteMode(value as RewriteMode)}
            options={[
              {
                value: "conservative",
                label: lang === "zh" ? "保守合规" : "Conservative",
              },
              {
                value: "balanced",
                label: lang === "zh" ? "平衡可读" : "Balanced",
              },
              {
                value: "conversion",
                label: lang === "zh" ? "转化友好" : "Conversion-friendly",
              },
            ]}
            disabled={rewriteLoading || rewriteSubmitting}
          />
          {rewriteLoading ? (
            <Text type="secondary">
              {lang === "zh" ? "正在生成预览..." : "Generating preview..."}
            </Text>
          ) : (
            <>
              <Text type="secondary">
                {lang === "zh"
                  ? "Pro 会员可解锁完整改写；当前展示免费预览片段。"
                  : "Pro unlocks full rewrite; free preview snippet is shown below."}
              </Text>
              <div className="review-item">
                <Text strong>
                  {lang === "zh"
                    ? "免费预览（部分）"
                    : "Free preview (partial)"}
                </Text>
                <Text>{rewritePreviewData?.preview_text ?? ""}</Text>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
